import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Settings, Mail, UserPlus, ClipboardList, Sparkles, LifeBuoy, ArrowRight,
  MailOpen, User as UserIcon, Loader2,
} from "lucide-react";

const STEPS = [
  {
    icon: Settings,
    title: "1 · Set up your show first",
    to: "/show-settings",
    linkLabel: "Open Show Settings",
    points: [
      "Show name, host name and your show description — these fill the {show_name} and {host_name} placeholders in every email.",
      "Your contact email — guest replies and reminders come to this address, so set it before anything else.",
      "Upload your podcast logo and pick your brand colors — they appear on your guest intake form, promo kit pages and emails.",
      "Add your social links (plus Apple Podcasts / Spotify pages so guests get review buttons on their promo kit).",
      "Optional: a show-level broadcast/studio link so you can copy it to each guest in one click.",
    ],
  },
  {
    icon: Mail,
    title: "2 · Put the emails in your voice",
    to: "/email-templates",
    linkLabel: "Open Email Templates",
    points: [
      "Six built-in templates: booking confirmation, intake form, intake reminder, pre-show check-in, host prep reminder (to you), and post-episode thank-you.",
      "On each of the four automated templates, the Send timing card lets you pick how many days before the recording it goes out — save the number and the whole show respects it.",
      "Edit the wording to sound like you — anything in {curly braces} is filled in automatically per guest. The side panel lists every placeholder and where it pulls from; click one to insert it.",
      "Made a mess? \"Reset to default\" restores the original at any time.",
    ],
  },
  {
    icon: UserPlus,
    title: "3 · Add your first guest",
    to: "/guests",
    linkLabel: "Open Guests",
    points: [
      "Just a name, email and episode date is enough. The moment you save, the booking confirmation email is sent automatically.",
      "The intake form link goes out later on its own — by default 14 days before the recording, editable per show.",
      "Each guest gets a 12-step checklist — every card shows the next action so you always know what's due.",
      "Add their StreamYard/studio link on the Info tab when you create the broadcast — the pre-show email won't go out without it (you'll get a Host Prep Reminder if it's missing).",
    ],
  },
  {
    icon: ClipboardList,
    title: "4 · The guest intake form",
    points: [
      "Each guest receives a personal, secure form link. It collects: name, headshot, one-sentence intro, website + LinkedIn + socials, and up to a few short story questions you customise per show.",
      "Everything they submit lands on the guest's Info tab automatically — and powers the AI promo generator + Show Prep.",
      "If a guest hasn't finished the form by the intake-reminder window (default 7 days out), a reminder email goes out automatically.",
      "Need something the form doesn't ask? Add it to the guest's Notes on their Info tab, or email support to request a new form field.",
    ],
  },
  {
    icon: Sparkles,
    title: "5 · Promo kit & the rest runs itself",
    points: [
      "One click generates 8 promo assets (episode title, descriptions, social posts, guest swipe copy, SEO YouTube description, guest email swipe) from the intake info + web enrichment.",
      "Upload episode graphics to the guest's Share Kit — their personal promo kit page gives them copy buttons, downloads and share links.",
      "Strict cadence: booking confirmation on save → intake form (default 14d out) → intake reminder if still incomplete (default 7d out) → pre-show check-in when the studio link is set (default 5d out) → post-episode thank-you.",
      "Watch it all happen in the Dashboard's Automation activity feed.",
    ],
  },
];

const PREVIEW_TEMPLATES = [
  { key: "booking_confirmation", label: "1 · Booking confirmation" },
  { key: "intake_form", label: "2 · Intake form" },
  { key: "form_reminder", label: "3 · Intake reminder" },
  { key: "preshow_checkin", label: "4 · Pre-show check-in" },
  { key: "day_of_reminder", label: "5 · Day-of reminder" },
  { key: "post_thankyou", label: "6 · Post-episode thank-you" },
];

function EmailPreview() {
  const [active, setActive] = useState("booking_confirmation");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    api.get(`/shows/me/email-preview/${active}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.detail || "Preview failed to load."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active]);

  return (
    <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-wine)" }} data-testid="guide-email-preview-card">
      <h2 className="pgm-display" style={{ fontSize: 22, display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <MailOpen size={19} color="var(--pgm-magenta)" /> See what your emails will look like
      </h2>
      <p style={{ fontSize: 13, opacity: 0.75, marginBottom: 14 }}>
        Rendered with your current show settings against a demo guest (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, verticalAlign: "middle" }}>
          <UserIcon size={12} color="var(--pgm-magenta)" /><em>Alex Sample</em>
        </span>
        , episode 14 days out). No real emails sent — this is just a preview.
      </p>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {PREVIEW_TEMPLATES.map(t => (
          <button
            key={t.key}
            className={`pgm-btn ${active === t.key ? "" : "pgm-btn-ghost"}`}
            style={{ padding: "6px 12px", fontSize: 11 }}
            onClick={() => setActive(t.key)}
            data-testid={`preview-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, opacity: 0.7, fontSize: 13 }}>
          <Loader2 size={14} className="animate-spin" /> Rendering preview…
        </div>
      )}
      {error && !loading && (
        <div className="pgm-callout" style={{ borderLeftColor: "var(--pgm-orange)" }} data-testid="preview-error">
          {error}
        </div>
      )}
      {data && !loading && !error && (
        <div style={{ background: "var(--pgm-cream)", border: "1.5px solid var(--pgm-rule)", padding: 16 }} data-testid="preview-body">
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4 }}>TO</div>
          <div style={{ fontSize: 13, marginBottom: 10 }}>{data.to}</div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4 }}>SUBJECT</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: "var(--pgm-wine)" }} data-testid="preview-subject">
            {data.subject}
          </div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4 }}>BODY</div>
          <pre style={{ fontFamily: "Arial, sans-serif", fontSize: 13.5, lineHeight: 1.55, whiteSpace: "pre-wrap", margin: 0, color: "var(--pgm-ink)" }}>
            {data.body}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function GettingStarted() {
  const [supportEmail, setSupportEmail] = useState("");
  useEffect(() => {
    api.get("/config").then(r => setSupportEmail(r.data.support_email)).catch((e) => console.warn("Config load failed:", e));
  }, []);

  return (
    <div className="pgm-reveal" style={{ maxWidth: 860 }}>
      <div className="pgm-micro">◆ Getting Started</div>
      <h1 className="pgm-display" style={{ fontSize: 44, marginTop: 4 }}>How to run your show here</h1>
      <p style={{ color: "var(--pgm-wine)", opacity: 0.75, marginBottom: 26 }}>
        Five steps, in this order — after that, guest management mostly runs itself.
      </p>

      {STEPS.map(({ icon: Icon, title, points, to, linkLabel }) => (
        <div key={title} className="pgm-card" style={{ marginBottom: 16, borderLeftColor: "var(--pgm-magenta)" }} data-testid={`guide-step-${title[0]}`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
            <h2 className="pgm-display" style={{ fontSize: 22, display: "flex", alignItems: "center", gap: 10 }}>
              <Icon size={19} color="var(--pgm-magenta)" /> {title}
            </h2>
            {to && (
              <Link to={to} className="pgm-btn pgm-btn-ghost" style={{ padding: "6px 12px", fontSize: 11, textDecoration: "none" }}>
                {linkLabel} <ArrowRight size={12} />
              </Link>
            )}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 7 }}>
            {points.map((p, i) => (
              <li key={i} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--pgm-ink)" }}>{p}</li>
            ))}
          </ul>
        </div>
      ))}

      <div style={{ marginTop: 8, marginBottom: 16 }}>
        <EmailPreview />
      </div>

      <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-olive)" }} data-testid="guide-support-card">
        <h2 className="pgm-display" style={{ fontSize: 22, display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <LifeBuoy size={19} color="var(--pgm-olive)" /> Stuck? We&rsquo;re a real human
        </h2>
        <p style={{ fontSize: 13.5, lineHeight: 1.55 }}>
          Any problem, question or feature request — email the app admin at{" "}
          {supportEmail ? (
            <a href={`mailto:${supportEmail}`} className="pgm-link" data-testid="guide-support-email">{supportEmail}</a>
          ) : "support"}
          {" "}and we&rsquo;ll get you sorted.
        </p>
      </div>
    </div>
  );
}
